import "./hardware-search-page.style.scss";

import { useResizeObserver } from "@amzn/stencil-react-components/hooks";
import { Col, Hr, Row, View } from "@amzn/stencil-react-components/layout";
import { Link, LinkTextDecorationOptions } from "@amzn/stencil-react-components/link";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import { PageMainContainer } from "@amzn/it-shop-shared-components";
import { NoResultsFound } from "@/common/components/NoResultsFound/no-result-found";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import { HardwareUpgradeInfoCard } from "@/pages/hardware-search/components/HardwareUpgradeInfoCard/hardware-upgrade-info-card";
import HardwareFacetedFilter from "@/pages/hardware-search/components/HardwareFacetedFilter/hardware-faceted-filter";
import SearchItemsList from "@/pages/hardware-search/components/SearchItemsList/search-items-list";
import { useHardwareCatalog } from "@/hooks/use-hardware-catalog";
import { useNotificationStore } from "@/stores/use-notification-store";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";

export const PAGE_LIMIT = 500;
const COLUMN_WIDTH = 297;

const SearchResultsHardwarePage: FC = () => {
  const { ref } = useResizeObserver<HTMLDivElement>();

  const [filters, setFilters] = useState<Record<string, string>>({ parentTaxonomyId: "-" });

  const searchParams = useMemo(
    () => ({
      limit: PAGE_LIMIT,
      offset: 0,
      sortBy: "name",
      sortDirection: "asc",
      filter: JSON.stringify(filters),
    }),
    [filters]
  );

  const { data: items, paginationInfo, error, isLoading } = useHardwareCatalog(searchParams);

  const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
  const [osOptions, setOsOptions] = useState<string[]>([]);
  const facetsInitialized = useRef(false);

  useEffect(() => {
    if (items && !facetsInitialized.current) {
      facetsInitialized.current = true;
      setCategoryOptions(
        [...new Set(items.map((item) => item.subCategory).filter((value): value is string => Boolean(value)))].sort()
      );
      setOsOptions(
        [...new Set(items.map((item) => item.os).filter((value): value is string => Boolean(value)))].sort()
      );
    }
  }, [items]);

  const addFacetFilter = (filter: { field: string; value: string }) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (filter.value) {
        next[filter.field] = filter.value;
      } else {
        const { [filter.field]: _, ...rest } = next;
        return rest;
      }
      return next;
    });
  };

  const addBanner = useNotificationStore((state) => state.addBanner);

  useEffect(() => {
    if (error) {
      console.error("Failed to load hardware catalog:", error);
      addBanner({ type: "error", message: getString("errors.loadingItems") });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- addBanner is a stable Zustand selector
  }, [error]);

  if (!items && isLoading) {
    return (
      <View padding="S700">
        <Spinner size={SpinnerSize.Medium} />
      </View>
    );
  }

  if (!items && error) {
    return null;
  }

  if (paginationInfo?.total === 0) {
    return (
      <PageMainContainer>
        <NoResultsFound searchTerm="hardware" />
      </PageMainContainer>
    );
  }

  return (
    <View className="search-hardware-results-container">
      <Row width="100%" height="100%">
        {/* Facets column */}
        <Col width={COLUMN_WIDTH} height="100%" backgroundColor="neutral05">
          <Col height="100%">
            <HardwareFacetedFilter
              filterName="subCategory"
              title="Category"
              options={categoryOptions}
              addFacetFilter={addFacetFilter}
            />
            <HardwareFacetedFilter
              filterName="os"
              title="Operating Systems"
              options={osOptions}
              addFacetFilter={addFacetFilter}
            />
          </Col>
          <Col padding={[0, 0, "S400", 0]} height="110px" justifyContent="flex-end">
            <Col width="70%" margin={["S500", 0, 0, "S500"]}>
              <Hr size="wide" color="neutral30" />
            </Col>
            <Text color="neutral70" margin={["S300", 0, 0, "S500"]} fontSize="T14">
              {getString("submitHardwareRequest.header")}
            </Text>
            <Link
              data-testid="request-hardware-link"
              target="_blank"
              textDecoration={LinkTextDecorationOptions.None}
              fontWeight="bold"
              href={getString("submitHardwareRequest.link")}
              color="primary80"
              margin={["S300", 0, 0, 0]}
              padding={[0, 0, 0, "S500"]}
              fontSize="T14"
              data-cy="submit-hardware-request-link"
            >
              {getString("submitHardwareRequest.linkText")}
            </Link>
          </Col>
        </Col>

        {/* Search results */}
        <Col className="search-results-column" padding="S500" gridGap="S500" ref={ref}>
          <HardwareUpgradeInfoCard />

          <SearchItemsList
            scrollableTarget="catalog-items-list-container"
            items={items ?? []}
            onSearchNext={() => undefined}
            shouldLoadMore={() => false}
            isLoading={isLoading}
            isLoadingNextItems={false}
            totalCount={paginationInfo?.total ?? 0}
          />
        </Col>
      </Row>
    </View>
  );
};

export default SearchResultsHardwarePage;
